import React, { useState } from 'react';
import { Alert, Button, Tag, Typography, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';

const { Text } = Typography;

const MAX_JSON_RECIPIENTS = 10_000;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface JsonRecipientUploadProps {
  onChange: (file: File | null, recipientCount: number | null, variables: string[]) => void;
}

/**
 * Upload de lista de destinatários em JSON (US3, FR-005). Prévia inteiramente
 * client-side (contagem + variáveis detectadas + validação antecipada) —
 * research.md §4: não há chamada a POST /admin/bulk-email/preview para esta
 * origem. O arquivo original (não o JSON já parseado) é o que é enviado ao
 * backend na confirmação do disparo, que faz sua própria validação.
 */
export const JsonRecipientUpload: React.FC<JsonRecipientUploadProps> = ({ onChange }) => {
  const [error, setError] = useState<string | null>(null);
  const [recipientCount, setRecipientCount] = useState<number | null>(null);
  const [variables, setVariables] = useState<string[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);

  const reset = () => {
    setError(null);
    setRecipientCount(null);
    setVariables([]);
    setFileName(null);
    onChange(null, null, []);
  };

  const beforeUpload = async (file: File) => {
    let parsed: unknown;
    try {
      const text = await file.text();
      parsed = JSON.parse(text);
    } catch {
      setError('Arquivo inválido: JSON malformado.');
      setRecipientCount(null);
      setVariables([]);
      onChange(null, null, []);
      return false;
    }

    if (!Array.isArray(parsed)) {
      setError('O arquivo JSON deve conter uma lista (array) de objetos.');
      onChange(null, null, []);
      return false;
    }

    if (parsed.length > MAX_JSON_RECIPIENTS) {
      setError(`O arquivo excede o limite máximo de ${MAX_JSON_RECIPIENTS} destinatários por upload.`);
      onChange(null, null, []);
      return false;
    }

    const seenEmails = new Set<string>();
    const variableKeys = new Set<string>();

    for (let i = 0; i < parsed.length; i++) {
      const record = parsed[i];
      if (typeof record !== 'object' || record === null || Array.isArray(record)) {
        setError(`Registro ${i + 1} inválido: cada item deve ser um objeto.`);
        onChange(null, null, []);
        return false;
      }
      const { email, ...rest } = record as Record<string, unknown>;
      if (typeof email !== 'string' || !EMAIL_REGEX.test(email)) {
        setError(`Registro ${i + 1}: campo "email" ausente ou em formato inválido.`);
        onChange(null, null, []);
        return false;
      }
      const normalized = email.trim().toLowerCase();
      if (seenEmails.has(normalized)) {
        setError(`E-mail duplicado no arquivo: ${email}`);
        onChange(null, null, []);
        return false;
      }
      seenEmails.add(normalized);
      Object.keys(rest).forEach((key) => variableKeys.add(key));
    }

    setError(null);
    setRecipientCount(parsed.length);
    setVariables(Array.from(variableKeys));
    setFileName(file.name);
    onChange(file, parsed.length, Array.from(variableKeys));
    return false;
  };

  return (
    <div>
      <Upload
        beforeUpload={beforeUpload}
        maxCount={1}
        accept="application/json,.json"
        showUploadList={false}
      >
        <Button icon={<UploadOutlined />}>Selecionar arquivo JSON</Button>
      </Upload>

      {error && <Alert type="error" message={error} style={{ marginTop: 12 }} />}

      {fileName && !error && (
        <div style={{ marginTop: 12 }}>
          <Text>
            Arquivo <strong>{fileName}</strong>: {recipientCount} destinatário(s) detectado(s).
          </Text>
          {' '}
          <Button size="small" type="link" onClick={reset}>
            Remover
          </Button>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">Variáveis disponíveis para uso no assunto/conteúdo: </Text>
            {variables.length > 0 ? (
              variables.map((variable) => (
                <Tag key={variable}>{`{{${variable}}}`}</Tag>
              ))
            ) : (
              <Text type="secondary">nenhuma (o arquivo só tem o campo email)</Text>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
